import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DELIVERY_ERROR_MESSAGE,
  TimeSlot,
  TimeSlotGroup,
  TimeSlotIntervalTypes,
  TimeSlotOption,
} from '@checkout-v2/const';
import { DeliveryAddressService, TimeSlotsService } from '@checkout-v2/feature';
import { SdModalService, SystemMessageComponent } from '@sdvor/shared/ui/v2';
import { CheckoutValidationErrorComponent } from '../../checkout-validation-error';
import { DeliveryAddressDialogComponent } from '../receipt-address/delivery/delivery-address-dialog';
import { TimeSlotDatesComponent } from './time-slot-dates';
import { TimeSlotGroupsComponent } from './time-slot-groups';
import { TimeSlotOptionsComponent } from './time-slot-options';

@Component({
  selector: 'sd-time-slots',
  imports: [
    TimeSlotDatesComponent,
    TimeSlotGroupsComponent,
    TimeSlotOptionsComponent,
    CheckoutValidationErrorComponent,
    SystemMessageComponent,
  ],
  templateUrl: './time-slots.component.html',
  styleUrl: './time-slots.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSlotsComponent {
  private readonly timeSlotsService = inject(TimeSlotsService);
  private readonly deliveryAddressService = inject(DeliveryAddressService);
  private readonly modalService = inject(SdModalService);

  protected readonly isLoading = toSignal(this.timeSlotsService.getTimeSlotsLoading());
  protected readonly isError = toSignal(this.timeSlotsService.getTimeSlotsError());
  protected readonly isSuccess = toSignal(this.timeSlotsService.getTimeSlotsSuccess());
  protected readonly timeSlots = toSignal(this.timeSlotsService.getTimeSlots());
  protected readonly deliveryAddress = toSignal(this.deliveryAddressService.activeAddress$);
  protected readonly customDateIsLoading = toSignal(this.timeSlotsService.getTimeSlotsForCustomDateLoading());
  protected readonly customDateIsError = toSignal(this.timeSlotsService.getTimeSlotsForCustomDateError());
  protected readonly customDateIsSuccess = toSignal(this.timeSlotsService.getTimeSlotsForCustomDateSuccess());
  private readonly selectedOptionFromStore = toSignal(this.timeSlotsService.getSelectedTimeSlot());
  private readonly selectedCustomDateFromStore = toSignal(this.timeSlotsService.getSelectedCustomDate());

  protected readonly errorMessage = DELIVERY_ERROR_MESSAGE;

  protected selectedTimeSlotDate = signal<string | undefined>(undefined);
  protected selectedTimeSlotAnotherDate = signal<string | undefined>(undefined);
  protected selectedTimeSlot = computed(() => {
    const timeSlots = this.timeSlots() ?? [];
    const selectedTimeSlotDate = this.selectedTimeSlotDate();

    return timeSlots.find((item) => item.date === selectedTimeSlotDate);
  });

  protected selectedGroupName = signal<string | undefined>(undefined);
  protected selectedGroup = computed(() => {
    const selectedTimeSlot = this.selectedTimeSlot();
    const selectedOptionsGroupName = this.selectedGroupName();

    if (!selectedTimeSlot || !selectedOptionsGroupName) {
      return undefined;
    }

    return selectedTimeSlot.groups.find((group) => group.name === selectedOptionsGroupName);
  });

  protected selectedOption = signal<TimeSlotOption | undefined>(undefined);

  constructor() {
    // синхронизация значения выбранного таймслота со стором
    effect(() => {
      const selectedOptionFromStore = this.selectedOptionFromStore();

      untracked(() => {
        // сбросить выбранную опцию, если еще не сброшена
        if (!selectedOptionFromStore && this.selectedOption()) {
          this.selectedTimeSlotDate.set(undefined);
          this.selectedGroupName.set(undefined);
          this.selectedOption.set(undefined);

          return;
        }

        // синхронизировать значение в компоненте со значением из стора
        if (selectedOptionFromStore && selectedOptionFromStore.id !== this.selectedOption()?.id) {
          const date = selectedOptionFromStore.interval.clientInterval.date;
          const timeSlots = this.timeSlots();

          if (timeSlots?.length) {
            const optionsGroup = this.findGroupByOptionId(selectedOptionFromStore.id, timeSlots);

            if (optionsGroup) {
              const optionsGroupName = optionsGroup.name;

              this.selectedTimeSlotDate.set(date);
              this.selectedGroupName.set(optionsGroupName);
              this.selectedOption.set(selectedOptionFromStore);
            }
          }
        }
      });
    });

    // синхронизация значения выбранной кастомной даты со стором
    effect(() => {
      const selectedCustomDateFromStore = this.selectedCustomDateFromStore();

      untracked(() => {
        if (selectedCustomDateFromStore !== this.selectedTimeSlotAnotherDate()) {
          this.selectedTimeSlotAnotherDate.set(selectedCustomDateFromStore);
        }
      });
    });
  }

  protected onSelectedDateChange(date: string | undefined): void {
    this.selectedTimeSlotDate.set(date);

    // сбросить выбранную группу если изменилась дата
    if (this.selectedGroupName()) {
      this.onSelectedGroupChange(undefined);
    }
  }

  protected onSelectedAnotherDateChange(date: string | undefined): void {
    this.selectedTimeSlotAnotherDate.set(date);

    if (date) {
      this.timeSlotsService.selectCustomDate(date);
    }
  }

  protected onSelectedGroupChange(name: string | undefined): void {
    this.selectedGroupName.set(name);

    // сбросить выбранную опцию если изменилась группа
    if (this.selectedOption()) {
      this.onSelectedOptionChange(undefined);
    }
  }

  protected onSelectedOptionChange(option: TimeSlotOption | undefined): void {
    this.selectedOption.set(option);

    if (option && option.interval.type === TimeSlotIntervalTypes.ExactlyInTime) {
      this.timeSlotsService.selectTimeSlot(option.id, { customTimeFromTo: option.interval.clientInterval.timeFromTo });
    } else if (option) {
      this.timeSlotsService.selectTimeSlot(option.id);
    } else {
      this.timeSlotsService.resetTimeSlot();
    }
  }

  protected openAddressModal(): void {
    const addressId = this.deliveryAddress()?.id;

    if (addressId) {
      this.deliveryAddressService.editableAddressId$.next(addressId);

      this.modalService.open(DeliveryAddressDialogComponent, {
        modalType: { xxs: 'fullscreen', lg: 'standard' },
        maxWidth: { lg: '1240px' },
      });
    }
  }

  private findGroupByOptionId(id: string, timeSlots: TimeSlot[]): TimeSlotGroup | undefined {
    const groups = timeSlots.reduce<TimeSlotGroup[]>((acc, timeSlot) => acc.concat(timeSlot.groups), []);

    return groups.find((group) => group.options.some((option) => option.id === id));
  }
}
